// hooks/useNotificationConfigurations.ts - Corrected to handle API response structure
import { useState, useEffect, useCallback } from 'react';
import { NotificationConfigurationDTO } from '../../api';

interface UseNotificationConfigurationsReturn {
    configurations: NotificationConfigurationDTO[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    getConfigurationByCode: (code: string) => NotificationConfigurationDTO | undefined;
    getActiveConfigurations: () => NotificationConfigurationDTO[];
}

export const useNotificationConfigurations = (): UseNotificationConfigurationsReturn => {
    const [configurations, setConfigurations] = useState<NotificationConfigurationDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchConfigurations = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch all configurations (active and inactive)
            const response = await fetch('/api/notification-configurations');

            if (!response.ok) {
                throw new Error(`Failed to fetch configurations: ${response.status} ${response.statusText}`);
            }

            const responseData = await response.json();

            // CORRECTED: Handle the response structure - check if response has .data property
            let configurationsData: NotificationConfigurationDTO[];

            if (responseData.data && Array.isArray(responseData.data)) {
                // If response is wrapped in { data: [...] }
                configurationsData = responseData.data;
            } else if (Array.isArray(responseData)) {
                // If response is directly an array
                configurationsData = responseData;
            } else {
                console.warn('Unexpected response structure:', responseData);
                configurationsData = [];
            }

            setConfigurations(configurationsData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
            console.error('Failed to fetch notification configurations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfigurations();
    }, [fetchConfigurations]);

    const getConfigurationByCode = useCallback((code: string) => {
        return configurations.find(config => config.code === code);
    }, [configurations]);

    const getActiveConfigurations = useCallback(() => {
        return configurations.filter(config => config.active);
    }, [configurations]);

    return {
        configurations,
        loading,
        error,
        refetch: fetchConfigurations,
        getConfigurationByCode,
        getActiveConfigurations
    };
};

// Utility functions for working with notification configurations in nodes
export const notificationConfigUtils = {
    /**
     * Convert NotificationConfigurationDTO to NotificationConfigurationSelection
     */
    toSelection: (config: NotificationConfigurationDTO) => ({
        code: config.code,
        name: config.name,
        platform: config.platform,
        active: config.active || false
    }),

    /**
     * Validate notification configuration codes against available configurations
     */
    validateCodes: (codes: string[], availableConfigs: NotificationConfigurationDTO[]) => {
        const availableCodes = availableConfigs.map(c => c.code);
        const invalidCodes = codes.filter(code => !availableCodes.includes(code));

        return {
            valid: invalidCodes.length === 0,
            invalidCodes,
            validCodes: codes.filter(code => availableCodes.includes(code))
        };
    },

    /**
     * Get platform color for UI display
     */
    getPlatformColor: (platform: string) => {
        switch (platform) {
            case 'EMAIL': return 'blue';
            case 'SMS': return 'green';
            case 'WHATS_APP': return 'orange';
            case 'PUSH': return 'purple';
            case 'IN_APP': return 'cyan';
            default: return 'default';
        }
    },

    /**
     * Format notification configuration for display
     */
    formatForDisplay: (config: NotificationConfigurationDTO) => ({
        label: `${config.name} (${config.code})`,
        value: config.code,
        platform: config.platform,
        active: config.active
    }),

    /**
     * Group configurations by platform
     */
    groupByPlatform: (configs: NotificationConfigurationDTO[]) => {
        return configs.reduce((groups, config) => {
            const platform = config.platform ?? 'UNKNOWN';
            if (!groups[platform]) {
                groups[platform] = [];
            }
            groups[platform].push(config);
            return groups;
        }, {} as Record<string, NotificationConfigurationDTO[]>);
    }
};

// Alternative hook using React Query if you're using it in your project
// This would automatically handle the .data extraction
export const useNotificationConfigurationsQuery = () => {
    // If you're using react-query, you might have something like:
    // const { data, isLoading, error, refetch } = useQuery('notification-configurations',
    //     () => fetch('/api/notification-configurations').then(res => res.json())
    // );

    // return {
    //     configurations: data?.data || data || [], // Handle both wrapped and unwrapped responses
    //     loading: isLoading,
    //     error: error?.message,
    //     refetch
    // };
};